import React, { useEffect, useState } from 'react';
import { X, MessageCircle, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile } from '../services/dataService';
import { notifyError, notifySuccess } from '../services/notify';
import { claimOneTimeModal } from '../services/oneTimeModals';

/**
 * One-time founder message shown on a user's next login after we deploy this.
 * Tracks dismissal via UserProfile.seenFounderMessageV1.
 *
 * To show a new message later: bump the version field on UserProfile (V1 → V2)
 * and update this component to check the new field.
 */
export const FounderMessageModal: React.FC = () => {
  const { userProfile, currentUser, refreshProfile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  // Show on first render if the user hasn't seen it yet.
  // Teachers and board admins only — students (anonymous or registered) do not see it.
  useEffect(() => {
    if (!userProfile || !currentUser) return;
    if (currentUser.isAnonymous) return;
    if (userProfile.role !== 'teacher' && userProfile.role !== 'board_admin') return;
    if (userProfile.seenFounderMessageV2) return;
    // Claim the one-time-dialog slot. Three of these exist now and they mount in
    // two different trees; two stacked on arrival means the one underneath is
    // dismissed unread and never returns.
    if (!claimOneTimeModal('founderMessage')) return;
    // Small delay so it doesn't flash during first page render
    const t = setTimeout(() => setIsOpen(true), 600);
    return () => clearTimeout(t);
  }, [userProfile, currentUser]);

  const markSeen = async () => {
    if (!currentUser) return;
    try {
      await updateUserProfile(currentUser.uid, {
        seenFounderMessageV2: new Date().toISOString(),
      });
      await refreshProfile();
    } catch (e) {
      console.error('Failed to mark founder message seen:', e);
    }
  };

  const handleDismiss = async () => {
    setIsOpen(false);
    await markSeen();
  };

  // Escape closes it too, like every other modal — so it can never trap a user.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !userProfile) return;
    setSending(true);
    try {
      const { getAuth } = await import('firebase/auth');
      const currentUser = getAuth().currentUser;
      if (!currentUser) {
        throw new Error('You must be signed in to send a reply.');
      }
      const idToken = await currentUser.getIdToken();
      const res = await fetch('/.netlify/functions/founder-reply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          fromEmail: userProfile.email,
          fromName: userProfile.displayName,
          message: replyText.trim(),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      notifySuccess('Thanks — message sent to Reece!');
      setReplyText('');
      setIsOpen(false);
      await markSeen();
    } catch (e) {
      console.error('Founder reply send failed:', e);
      notifyError('Could not send your message. You can email reece@levelupclassroom.com directly.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 overflow-y-auto"
      onClick={handleDismiss}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-500 px-6 py-8 relative flex-shrink-0">
          <button
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="absolute top-3 right-3 p-2 rounded-lg text-white/80 hover:bg-white/20 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <MessageCircle size={22} className="text-white" />
            </div>
            <div>
              <p className="text-white/80 text-xs uppercase tracking-wide font-semibold">A note from Reece</p>
              <h2 className="text-white text-xl font-bold">Level Up Classroom 2.0 👋</h2>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <p className="text-gray-700 leading-relaxed">
            A lot has shipped since we started. Here is what is new:
          </p>
          <ul className="space-y-2 text-gray-700 text-sm leading-relaxed">
            <li><strong>AI grading</strong> — drop in student work (even multi-page PDFs) and get a suggested mark plus feedback to review and adjust. Now on Pro and Ultra.</li>
            <li><strong>Google Classroom, both directions</strong> — import assignments, and push grades and posts (with attachments) back.</li>
            <li><strong>Proctored exams</strong> — build a test or turn a PDF into one, run it full screen with anti-cheat, shuffle question order per student, and schedule when it opens.</li>
            <li><strong>Report cards</strong> — write and sign off comments, then release them to students and parents.</li>
            <li><strong>Parent portal</strong> — parents see grades, attendance, a category breakdown, and the report card.</li>
            <li><strong>Daily games, expanded</strong> — four new games, weekly challenges, and login streaks.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed">
            Thanks for building this with us. What is working, what is frustrating, what is missing? I read every message personally and often ship fixes the same week.
          </p>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
              Quick message (optional)
            </label>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="What's on your mind?"
              rows={4}
              maxLength={2000}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">
              Or email directly: <strong>reece@levelupclassroom.com</strong>
            </p>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-end gap-2 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            Maybe later
          </button>
          <button
            onClick={handleSendReply}
            disabled={!replyText.trim() || sending}
            className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-xl transition-colors flex items-center gap-2"
          >
            {sending ? 'Sending...' : <><Send size={14} /> Send</>}
          </button>
        </div>
      </div>
    </div>
  );
};
